import type { NavigationGuard } from 'vue-router';
import { missingPermissionFor } from './route-permission.js';
import { useAuthStore } from './store.js';

/**
 * Navigation guard, *fail-closed*.
 *
 * Two questions, in this order: is there a session, and does it hold what the
 * route asks for. Only a route declaring `meta.public === true` on itself gets
 * through the first one without a session. Any other value —missing, `false`,
 * inherited from a parent— asks for a session, so that forgetting the flag never
 * opens a screen.
 *
 * Permissions work the other way round: a route without `meta.permission` needs
 * none. The default has to be that way because most screens are for everyone
 * with a session, and a route that guards something says so.
 *
 * When the permission is missing it goes to the 403 screen and **not** home:
 * being dropped somewhere else with no explanation reads as a bug, and the
 * person retries. The screen says what is missing and offers a way out.
 *
 * This is interface convenience, not security: the backend is the authority and
 * answers 401 or 403 even when the guard gets it wrong.
 */
export const authGuard: NavigationGuard = async (to) => {
  const auth = useAuthStore();
  await auth.ensureLoaded();

  const isPublic = to.meta.public === true;

  if (isPublic) {
    // With a session there is nothing to log into: go home instead.
    return to.name === 'login' && auth.status === 'authenticated' ? { name: 'home' } : true;
  }

  if (auth.status !== 'authenticated') {
    return {
      name: 'login',
      query: to.fullPath && to.fullPath !== '/' ? { redirect: to.fullPath } : {},
    };
  }

  const missing = missingPermissionFor(
    to.matched.map((route) => route.meta),
    auth.can,
  );

  if (missing !== undefined) {
    return { name: 'forbidden', query: { permission: missing, from: to.fullPath } };
  }

  return true;
};
